import { useState, useEffect } from "react";
import { useLocation,useParams } from "react-router-dom";
import Header from "../../../Components/adminDashboard/Header/Header";
import Swal from "sweetalert2";
import combinedServices from "../../../shared/services/user-service";

const PaypalManagement = () => {
    const location = useLocation();
    const { storeName = "All Stores", startDate: passedStartDate } = location.state || {};
    const { storeId } = useParams();
    
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({
        startDate: passedStartDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [transactionId, setTransactionId] = useState("");
    const [dateError, setDateError] = useState("");
    const [expandedInvoices, setExpandedInvoices] = useState({});

    const isDateRangeValid = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 31; // 31 days maximum (about 1 month)
    };

    const fetchTransactions = async () => {
        try {
            if (!isDateRangeValid(dateRange.startDate, dateRange.endDate)) {
                setDateError("Date range cannot exceed 1 month");
                return;
            }
            setDateError("");

            setLoading(true);
            // Get user's timezone offset and convert to PayPal's expected timezone (US Pacific)
            const userTimezoneOffset = new Date().getTimezoneOffset(); // in minutes
            const pacificTimezoneOffset = 8 * 60; // US Pacific is UTC-8 (480 minutes)
            
            // Calculate the difference between user's timezone and Pacific timezone
            const timezoneAdjustment = (userTimezoneOffset - pacificTimezoneOffset) * 60 * 1000; // convert to milliseconds
            
            const startDateLocal = new Date(`${dateRange.startDate}T00:00:00`);
            const endDateLocal = new Date(`${dateRange.endDate}T23:59:59`);
            
            // Adjust dates to Pacific timezone
            const adjustedStartDate = new Date(startDateLocal.getTime() + timezoneAdjustment);
            const adjustedEndDate = new Date(endDateLocal.getTime() + timezoneAdjustment);
            
            const data = {
                storeid: parseInt(storeId),
                startDate: adjustedStartDate.toISOString(),
                endDate: adjustedEndDate.toISOString(),
                ...(transactionId && { transactionId }),
            };
            const response = await combinedServices.getaccesstoken(data);
            console.log("API Response:", response);
            if (response.data.data.transaction_details) {
                const transactions = response.data.data.transaction_details || [];
                setTransactions(transactions);
                
                // Initialize expanded state for all invoice groups
                const invoiceGroups = groupTransactionsByInvoice(transactions);
                const initialExpandedState = {};
                Object.keys(invoiceGroups).forEach(invoiceId => {
                    initialExpandedState[invoiceId] = false;
                });
                setExpandedInvoices(initialExpandedState);
            } 
            else if(response.data.data.status == 400) {
                Swal.fire({
                    title: "Invalid Request",
                    text: response.data.data.message,
                    icon: "error"
                });
            }
            else if(response.data.data.status == 401) {
                Swal.fire({
                    title: "Unauthorized (Please Check the API credentials)",
                    text: response.data.data.message,
                    icon: "error"
                });
            }
                else if(response.data.data.status == 403) {
                    Swal.fire({
                    title: "Authorization Failed",
                    html: `
                        <div style="text-align: left; padding: 15px;">
                            <p style="margin-bottom: 12px; color: #721c24; font-weight: 500;">Authorization failed due to insufficient permissions.</p>
                            <p style="margin-bottom: 15px; font-weight: 600; color: #333;">Follow these steps to enable Transaction Search:</p>
                            
                            <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                                <div style="margin-bottom: 10px; padding: 8px 12px; background: white; border-left: 3px solid #007bff; border-radius: 3px;">
                                    <strong style="color: #007bff;">1.</strong> Log into <a href="https://developer.paypal.com/" target="_blank" style="color: #007bff;">PayPal Developer Dashboard</a>
                                </div>
                                
                                <div style="margin-bottom: 10px; padding: 8px 12px; background: white; border-left: 3px solid #28a745; border-radius: 3px;">
                                    <strong style="color: #28a745;">2.</strong> Go to <strong>Apps & Credentials</strong> → Select your REST API app
                                </div>
                                
                                <div style="padding: 8px 12px; background: white; border-left: 3px solid #ffc107; border-radius: 3px;">
                                    <strong style="color: #e67e22;">3.</strong> Enable <strong>Transaction Search</strong> in App Settings → Save
                                </div>
                            </div>
                            
                            <div style="background: #fff3cd; padding: 10px 12px; border-radius: 4px; border-left: 3px solid #ffc107;">
                                <small style="color: #856404;"><strong>⚠️ Note:</strong> Changes may take up to 9 hours to take effect.</small>
                            </div>
                        </div>
                    `,
                    icon: "warning",
                    confirmButtonText: "Got it",
                    width: 600
                });               
                 setTransactions([]);
                setExpandedInvoices({});
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            Swal.fire("Error", "Failed to fetch transactions", "error");
            setTransactions([]);
            setExpandedInvoices({});
        } finally {
            setLoading(false);
        }
    };

    // Group transactions by invoice ID
    const groupTransactionsByInvoice = (transactions) => {
        return transactions.reduce((acc, transaction) => {
            const invoiceId = transaction?.transaction_info?.invoice_id || 'no-invoice';
            if (!acc[invoiceId]) {
                acc[invoiceId] = [];
            }
            acc[invoiceId].push(transaction);
            return acc;
        }, {});
    };

    // Get primary description from the first item in cart
    const getPrimaryDescription = (transaction) => {
        const items = transaction?.cart_info?.item_details || [];
        if (items.length > 0) {
            return items[0].item_name || 'N/A';
        }
        return 'N/A';
    };

    // Get primary status from the transaction
    const getPrimaryStatus = (transaction) => {
        const status = transaction?.transaction_info?.transaction_status;
        if (status === 'S') return 'Success';
        if (status === 'P') return 'Pending';
        return status || 'N/A';
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        if (status === 'Success') return 'success';
        if (status === 'Pending') return 'pending';
        return 'other';
    };

    const toggleInvoiceAccordion = (invoiceId) => {
        setExpandedInvoices(prev => ({
            ...prev,
            [invoiceId]: !prev[invoiceId]
        }));
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => {
            const newDateRange = {
                ...prev,
                [name]: value
            };

            // Validate the new date range immediately
            if (!isDateRangeValid(newDateRange.startDate, newDateRange.endDate)) {
                setDateError("Date range cannot exceed 1 month");
            } else {
                setDateError("");
            }

            return newDateRange;
        });
    };

    const handleTransactionIdChange = (e) => {
        setTransactionId(e.target.value.trim()); // Trim whitespace
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchTransactions();
    };

    const handleClearFilters = () => {
        setTransactionId("");
        setDateRange({
            startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
        });
        setDateError("");
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    // Get grouped transactions
    const invoiceGroups = groupTransactionsByInvoice(transactions);

    return (
        <>
            <Header />
            <div className="dbAdminRight newDbRight">
                <div className="dbMid dbInner">
                    <div className="dbMainHd">PayPal Transaction Management - {storeName}</div>

                    <div className="catFilterWrp">
                        <form onSubmit={handleSubmit}>
                            <div className="catFilterCont">
                                <div className="filWrp" style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div style={{ minWidth: '200px' }}>
                                        <label className="label2">Transaction ID</label>
                                        <input
                                            type="text"
                                            value={transactionId}
                                            onChange={handleTransactionIdChange}
                                            className="form_ctrl"
                                            placeholder="Enter transaction ID"
                                        />
                                    </div>
                                    <div>
                                        <label className="label2">From Date</label>
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={dateRange.startDate}
                                            onChange={handleDateChange}
                                            className="form_ctrl"
                                            max={dateRange.endDate}
                                        />
                                    </div>
                                    <div>
                                        <label className="label2">To Date</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={dateRange.endDate}
                                            onChange={handleDateChange}
                                            className="form_ctrl"
                                            min={dateRange.startDate}
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                        <button
                                            type="submit"
                                            className="nambaBtn"
                                            style={{ marginLeft: "0px", marginTop: "10px" }}
                                            disabled={!!dateError || loading}
                                        >
                                            {loading ? 'Loading...' : 'Filter'}
                                        </button>
                                        <button
                                            type="button"
                                            className="nambaBtn"
                                            style={{ marginLeft: "0px" }}
                                            onClick={handleClearFilters}
                                            disabled={loading}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                                {dateError && (
                                    <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
                                        {dateError}
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="listTablWrp" >
                        {loading ? (
                            <div className="loading">Loading transactions...</div>
                        ) : Object.keys(invoiceGroups).length > 0 ? (
                            <div className="tblWrp" >
                                <table >
                                    <thead >
                                        <tr>
                                            <th>Invoice ID</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Total Transactions</th>
                                            <th>Total Amount</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody >
                                        {Object.entries(invoiceGroups).map(([invoiceId, transactions]) => {
                                            // Calculate total amount for this invoice group
                                            const totalAmount = transactions.reduce((sum, transaction) => {
                                                const amount = parseFloat(transaction?.transaction_info?.transaction_amount?.value || 0);
                                                return sum + amount;
                                            }, 0);
                                            
                                            // Get currency (assuming all transactions in group have same currency)
                                            const currency = transactions[0]?.transaction_info?.transaction_amount?.currency_code || 'USD';
                                            
                                            // Get primary description and status from first transaction
                                            const primaryDescription = getPrimaryDescription(transactions[0]);
                                            const primaryStatus = getPrimaryStatus(transactions[0]);
                                            const statusBadgeClass = getStatusBadgeClass(primaryStatus);
                                            
                                            return (
                                                <>
                                                    <tr  key={`header-${invoiceId}`}>
                                                        <td style={{padding:'15px 20px'}}>{invoiceId === 'no-invoice' ? 'N/A' : invoiceId}</td>
                                                        <td> {primaryDescription}</td>
                                                        <td>
                                                            <span className={`status-badge ${statusBadgeClass}`}>
                                                                {primaryStatus}
                                                            </span>
                                                        </td>
                                                        <td style={{padding:'15px 40px'}}>{transactions.length}</td>
                                                        <td style={{padding:'15px 0px'}}>{currency} {totalAmount.toFixed(2)}</td>
                                                        <td>
                                                            <button 
                                                                className="nambaBtn small"
                                                                style={{marginLeft:'0px'}}
                                                                onClick={() => toggleInvoiceAccordion(invoiceId)}
                                                            >
                                                                {expandedInvoices[invoiceId] ? 'Hide' : 'Show'} Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedInvoices[invoiceId] && (
                                                        <tr  key={`details-${invoiceId}`}>
                                                            <td colSpan="6">
                                                                <table className="sub-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Transaction ID</th>
                                                                            <th>Date</th>
                                                                            <th>Description</th>
                                                                            <th>Amount</th>
                                                                            <th>Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {transactions.map((transaction, index) => {
                                                                            const info = transaction?.transaction_info || {};
                                                                            const itemsInfo = transaction?.cart_info?.item_details || [];

                                                                            // Skip rendering if no transaction info exists
                                                                            if (!info || Object.keys(info).length === 0) return null;

                                                                            return (
                                                                                <tr key={`${invoiceId}-${index}`}>
                                                                                    <td>{info.transaction_id || 'N/A'}</td>
                                                                                    <td>
                                                                                        {info.transaction_initiation_date
                                                                                            ? new Date(info.transaction_initiation_date).toLocaleDateString()
                                                                                            : 'N/A'}
                                                                                    </td>
                                                                                    <td>
                                                                                        {itemsInfo.length > 0 ? (
                                                                                            itemsInfo.map((item, i) => (
                                                                                                <div key={i}>
                                                                                                    {item?.item_name || 'N/A'}
                                                                                                    ( Qty: {item?.item_quantity || '0'})
                                                                                                </div>
                                                                                            ))
                                                                                        ) : (
                                                                                            'N/A'
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        {info.transaction_amount
                                                                                            ? `${info.transaction_amount.currency_code || 'USD'} ${info.transaction_amount.value || '0'}`
                                                                                            : 'N/A'}
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className={`status-badge ${info.transaction_status === 'S' ? 'success' :
                                                                                            info.transaction_status === 'P' ? 'pending' : 'other'
                                                                                            }`}>
                                                                                            {info.transaction_status === 'S' ? 'Success' :
                                                                                                info.transaction_status === 'P' ? 'Pending' : info.transaction_status || 'N/A'}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data">
                                {dateError ? 'Please correct the date range' : 'No transactions found for the selected criteria'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .sub-table {
                    width: 100%;
                    margin: 10px 0;
                    border-collapse: collapse;
                    background-color: #f9f9f9;
                }
                .sub-table th, .sub-table td {
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                }
                .sub-table th {
                    background-color: #f1f1f1;
                }
                .status-badge {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.success {
                    background-color: #d4edda;
                    color: #155724;
                }
                .status-badge.pending {
                    background-color: #fff3cd;
                    color: #856404;
                }
                .status-badge.other {
                    background-color: #f8d7da;
                    color: #721c24;
                }
                .nambaBtn.small {
                    padding: 5px 10px;
                    font-size: 13px;
                }
            `}</style>
        </>
    );
};

export default PaypalManagement;